import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

const PAINT_SELECTOR =
  '.paintBlue, .paintYellow, .paintOrange, .paintPink, .paintGreen, .paintPurple'

const normalizeText = (text) => text.replace(/\s+/g, ' ').trim()
const MIN_LINE_TOLERANCE = 2

function applyBrushStrokeToElement(el, { force = false } = {}) {
  const hasBrushLine = !!el.querySelector('.brush-line')
  const sourceText =
    hasBrushLine && el.dataset.brushSource ? el.dataset.brushSource : (el.textContent || '')
  const normalizedText = normalizeText(sourceText)
  if (!normalizedText) return

  const existingLine = el.querySelector('.brush-line')
  if (
    !force &&
    el.dataset.brushProcessed === 'true' &&
    el.dataset.brushSource === normalizedText &&
    existingLine
  ) {
    return
  }

  const words = normalizedText.split(' ')

  // Replace existing content with per-word spans
  el.textContent = ''
  const spans = []

  words.forEach((word, i) => {
    const span = document.createElement('span')
    span.textContent = word + (i === words.length - 1 ? '' : ' ')
    span.dataset.word = word
    el.appendChild(span)
    spans.push(span)
  })

  // Force layout so positions are valid
  void el.offsetWidth

  const measurements = spans.map((span) => ({
    span,
    rect: span.getBoundingClientRect(),
  }))

  // Group into visual lines based on vertical position
  const lineGroups = []
  let currentLine = []
  let currentTop = null
  let currentHeight = null

  measurements.forEach(({ span, rect }) => {
    if (currentTop === null) {
      currentTop = rect.top
      currentHeight = rect.height
      currentLine.push(span)
      return
    }

    const threshold = Math.max(MIN_LINE_TOLERANCE, currentHeight * 0.5)
    if (rect.top - currentTop > threshold) {
      lineGroups.push(currentLine)
      currentLine = [span]
      currentTop = rect.top
      currentHeight = rect.height
      return
    }

    currentLine.push(span)
    currentHeight = Math.max(currentHeight, rect.height)
  })

  if (currentLine.length) {
    lineGroups.push(currentLine)
  }

  const lines = lineGroups.map((group) => {
    const line = document.createElement('span')
    line.className = 'brush-line'
    group.forEach((span, index) => {
      const word = span.dataset.word || span.textContent.trim()
      span.textContent = word + (index < group.length - 1 ? ' ' : '')
      line.appendChild(span)
    })
    return line
  })

  // Replace original content with line wrappers
  el.textContent = ''
  lines.forEach((line) => el.appendChild(line))

  el.dataset.brushProcessed = 'true'
  el.dataset.brushText = normalizedText
  el.dataset.brushSource = normalizedText
}

export const BrushStrokeProvider = ({ children }) => {
  useEffect(() => {
    const root = document.getElementById('___gatsby')
    if (!root) return

    let scheduled = false
    let forceNext = false
    const observed = new Set()
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
          scheduleProcess(true)
        })

    const observer = new MutationObserver(() => {
      scheduleProcess(true)
    })

    const observeRoot = () => {
      observer.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    const processAll = (force = false) => {
      observer.disconnect()
      try {
        const elements = Array.from(root.querySelectorAll(PAINT_SELECTOR))
        elements.forEach((el) => {
          applyBrushStrokeToElement(el, { force })
        })
        if (resizeObserver) {
          const nextObserved = new Set()
          elements.forEach((el) => {
            nextObserved.add(el)
            if (!observed.has(el)) resizeObserver.observe(el)
          })
          observed.forEach((el) => {
            if (!nextObserved.has(el)) resizeObserver.unobserve(el)
          })
          observed.clear()
          nextObserved.forEach((el) => observed.add(el))
        }
      } finally {
        observeRoot()
      }
    }

    const scheduleProcess = (force = false) => {
      if (force) forceNext = true
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        const runForce = forceNext
        forceNext = false
        processAll(runForce)
      })
    }

    scheduleProcess()
    observeRoot()

    const handleResize = () => scheduleProcess(true)
    window.addEventListener('resize', handleResize)

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => scheduleProcess(true)).catch(() => {})
    }

    return () => {
      observer.disconnect()
      if (resizeObserver) resizeObserver.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return <>{children}</>
}

BrushStrokeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default BrushStrokeProvider
